import React, { useEffect, useState } from 'react';
import MmSelect from './MmSelect';

/**
 * Recherche + filtres — selects via MmSelect (sheet mobile / menu glass desktop).
 */
const SearchAndFilterBar = ({
  searchValue = '',
  onSearchChange,
  filters = [],
  placeholder = 'Rechercher...',
  showSearch = true,
  searchInputClassName = '',
  collapseSearchOnMobile = false,
  className = '',
  mobileFiltersToggleLabel,
  variant = 'portal',
}) => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(() =>
    Boolean(collapseSearchOnMobile && String(searchValue || '').trim())
  );

  useEffect(() => {
    if (collapseSearchOnMobile && String(searchValue || '').trim()) {
      setMobileSearchOpen(true);
    }
  }, [collapseSearchOnMobile, searchValue]);

  const searchVisibleMdDown = !collapseSearchOnMobile || mobileSearchOpen;
  const shellClass =
    variant === 'mm'
      ? 'mm-card space-y-3 p-3 sm:p-4 sm:space-y-4'
      : 'entrea-portal-panel space-y-3 p-3 sm:p-4 sm:space-y-4';

  return (
    <div className={`${shellClass} ${className}`.trim()}>
      {collapseSearchOnMobile && showSearch && (
        <button
          type="button"
          onClick={() => setMobileSearchOpen((o) => !o)}
          className="mm-press flex w-full items-center justify-between rounded-xl border border-dashed border-slate-300/90 bg-white/80 px-3 py-2.5 text-left transition hover:bg-white md:hidden min-h-[44px] touch-manipulation"
        >
          <span className="text-sm font-medium text-slate-700">
            {mobileSearchOpen ? 'Masquer la recherche' : 'Rechercher dans la liste…'}
          </span>
          <svg
            className={`h-5 w-5 shrink-0 text-slate-500 transition-transform ${mobileSearchOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      )}
      {showSearch && (
        <div
          className={`relative ${collapseSearchOnMobile ? (searchVisibleMdDown ? 'block' : 'hidden md:block') : 'block'}`}
        >
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <svg className="h-5 w-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
          <input
            type="text"
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={placeholder}
            className={`mm-field block w-full !min-h-[44px] touch-manipulation pl-10 pr-10 ${searchInputClassName}`.trim()}
          />
          {searchValue ? (
            <button
              type="button"
              onClick={() => onSearchChange('')}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          ) : null}
        </div>
      )}

      {filters.length > 0 ? (
        <>
          <button
            type="button"
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            className="mm-press flex w-full items-center justify-between rounded-xl border border-slate-200/90 bg-slate-50/90 px-4 py-3 text-left transition hover:bg-slate-100 active:scale-[0.99] md:hidden min-h-[48px]"
          >
            <span className="text-sm font-semibold text-slate-800">
              {mobileFiltersToggleLabel || 'Filtres et affichage'}
            </span>
            <svg
              className={`h-5 w-5 text-slate-500 transform transition-transform ${isFilterOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          <div
            className={`grid grid-cols-1 gap-3 md:grid md:grid-cols-2 md:gap-4 lg:grid-cols-3 ${
              isFilterOpen ? 'grid' : 'hidden md:grid'
            }`}
          >
            {filters.map((filter) => (
              <div
                key={filter.key}
                className={`flex flex-col ${filter.hiddenOnMobile ? 'max-md:hidden' : ''}`}
              >
                <label className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                  {filter.label}
                </label>
                {filter.type === 'select' ? (
                  <MmSelect
                    value={filter.value ?? ''}
                    onChange={(v) => filter.onChange(v)}
                    options={(filter.options || []).map((option) => ({
                      value: String(option.value ?? ''),
                      label: String(option.label ?? option.value ?? ''),
                    }))}
                    placeholder={filter.label}
                    searchable={Boolean(filter.searchable) || (filter.options || []).length > 8}
                  />
                ) : filter.type === 'button-group' ? (
                  <div className="flex flex-wrap gap-2">
                    {filter.options.map((option, idx) => (
                      <button
                        key={`${filter.key}-btn-${idx}-${String(option.value ?? '')}`}
                        type="button"
                        onClick={() => filter.onChange(option.value)}
                        className={`mm-chip min-h-[40px] touch-manipulation px-3 ${
                          filter.value === option.value ? 'mm-chip--on' : 'mm-chip--off'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
};

export default SearchAndFilterBar;
